/* eslint-disable max-len */
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";

interface PullToRefreshProps {
  children: ReactNode;
}

const restrictedRoutesForPullToRefresh: any = [];

const PULL_THRESHOLD_PX = 250; // distance required to trigger refresh
const MAX_PULL_PX = 300; // purely visual cap for the indicator
const PULL_START_PX = 100; // dead-zone: do nothing until user pulls at least this far

function isWebViewEnv() {
  if (globalThis.isReactNativeWebview === "true") return true;
  if (globalThis.isReactNativeWebview2 === "yes") return true;
  return false;
}

function getRouteKeyFromPathname(pathname: string) {
  return pathname?.split("/")?.join("") ?? "";
}

function isModalOpen() {
  const bodyStyle = window.getComputedStyle(document.body);
  return bodyStyle.overflow === "hidden" || bodyStyle.overflowY === "hidden";
}

function applyResistance(rawPull: number) {
  // Light resistance so the indicator feels iOS-like, but we still let native bounce happen.
  if (rawPull <= 0) return 0;
  const resisted = rawPull * 0.55;
  return Math.min(resisted, MAX_PULL_PX);
}

const PullToRefreshComponent: React.FC<PullToRefreshProps> = ({ children }) => {
  const [pullPx, setPullPx] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isWebview = useMemo(() => isWebViewEnv(), []);

  const pullToRefreshIsTurnedOff = useMemo(() => {
    const routeKey = getRouteKeyFromPathname(window.location.pathname);
    return !isWebview || restrictedRoutesForPullToRefresh.includes(routeKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWebview, window.location.pathname]);

  const readyToRefresh =
    !pullToRefreshIsTurnedOff &&
    pullPx >= PULL_START_PX &&
    pullPx - PULL_START_PX >= PULL_THRESHOLD_PX - PULL_START_PX;

  const startYRef = useRef<number | null>(null);
  const pullingRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const lastPullRef = useRef(0);

  const setPullPxRaf = (next: number) => {
    lastPullRef.current = next;
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      setPullPx(lastPullRef.current);
    });
  };

  useEffect(() => {
    if (pullToRefreshIsTurnedOff) {
      setPullPx(0);
      setIsPulling(false);
      setIsRefreshing(false);
      startYRef.current = null;
      pullingRef.current = false;
      return;
    }

    const onTouchStart = (ev: TouchEvent) => {
      if (isRefreshing) return;

      // Don't allow pull-to-refresh when a modal is open (body overflow hidden).
      if (isModalOpen()) return;

      // Only start tracking if we're already at top.
      if (window.scrollY > 0) {
        startYRef.current = null;
        pullingRef.current = false;
        // Ensure the indicator is not left visible from a previous gesture.
        lastPullRef.current = 0;
        setPullPx(0);
        setIsPulling(false);
        return;
      }

      // New pull gesture: clear any previous pull state
      lastPullRef.current = 0;
      setPullPx(0);

      startYRef.current = ev.touches[0]?.clientY ?? null;
      pullingRef.current = true;
      setIsPulling(true);
    };

    const onTouchMove = (ev: TouchEvent) => {
      if (!pullingRef.current) return;
      if (isRefreshing) return;

      // Stop pull-to-refresh if a modal was opened mid-gesture.
      if (isModalOpen()) {
        pullingRef.current = false;
        startYRef.current = null;
        setIsPulling(false);
        setPullPxRaf(0);
        return;
      }

      // If user scrolled down (content moved), stop pull-to-refresh tracking.
      if (window.scrollY > 0) {
        pullingRef.current = false;
        startYRef.current = null;
        setIsPulling(false);
        setPullPxRaf(0);
        return;
      }

      const startY = startYRef.current;
      if (startY == null) return;

      const currentY = ev.touches[0]?.clientY ?? startY;
      const rawPull = currentY - startY;

      if (rawPull <= 0) {
        lastPullRef.current = 0;
        setPullPxRaf(0);
        return;
      }

      // Dead-zone: don't show/rotate until user pulled enough.
      if (rawPull < PULL_START_PX) {
        lastPullRef.current = 0;
        setPullPxRaf(0);
        return;
      }

      // IMPORTANT: do NOT preventDefault here.
      // We want the native iOS rubber-band (bounce) to remain intact.
      const nextPull = applyResistance(rawPull);
      setPullPxRaf(nextPull);
    };

    const finish = () => {
      pullingRef.current = false;
      startYRef.current = null;
      setIsPulling(false);

      if (isRefreshing) return;

      const shouldRefresh =
        lastPullRef.current >= PULL_START_PX &&
        lastPullRef.current - PULL_START_PX >=
          PULL_THRESHOLD_PX - PULL_START_PX;

      if (shouldRefresh) {
        setIsRefreshing(true);
        // Hide indicator and let the iOS bounce settle before reloading.
        lastPullRef.current = 0;
        setPullPx(0);
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        window.setTimeout(() => {
          window.location.reload();
        }, 350);
        return;
      }

      setPullPx(0);
    };

    const onTouchEnd = () => finish();
    const onTouchCancel = () => finish();

    const onScroll = () => {
      if (isRefreshing) return;
      if (pullingRef.current) return;
      if (window.scrollY > 0 && lastPullRef.current !== 0) {
        lastPullRef.current = 0;
        setPullPx(0);
        setIsPulling(false);
      }
    };

    // Attach to window so we follow the real page scroll/bounce.
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchCancel, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onTouchStart as any);
      window.removeEventListener("touchmove", onTouchMove as any);
      window.removeEventListener("touchend", onTouchEnd as any);
      window.removeEventListener("touchcancel", onTouchCancel as any);
      window.removeEventListener("scroll", onScroll as any);
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullToRefreshIsTurnedOff, isRefreshing, window.location.pathname]);

  // On regular web (non-webview), skip all pull-to-refresh rendering.
  if (!isWebview) {
    return (
      <div>
        <div>{children}</div>
      </div>
    );
  }

  // Derived values for indicator UI
  const effectivePullPx = Math.max(0, pullPx - PULL_START_PX);
  const effectiveThresholdPx = PULL_THRESHOLD_PX - PULL_START_PX;

  const progress = Math.max(
    0,
    Math.min(effectivePullPx / effectiveThresholdPx, 1)
  );
  const rotationDeg = progress * 360;

  // Only show while actively pulling (or while refreshing)
  const showIndicator =
    !pullToRefreshIsTurnedOff &&
    ((isPulling && effectivePullPx > 0) || isRefreshing);

  // Move the indicator down slightly as the user pulls.
  const indicatorTop = isRefreshing
    ? "100px"
    : effectivePullPx > 0
    ? `${80 + Math.min(effectivePullPx, 120) * 0.35}px`
    : "-70px";

  return (
    <div>
      <div>{children}</div>

      {showIndicator && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[1000] bg-mono-white rounded-[32px]"
          style={{
            top: indicatorTop,
            transition:
              isPulling || isRefreshing ? "none" : "top 200ms ease-in-out"
          }}
        >
          <RefreshIcon
            color="inherit"
            sx={{
              width: "64px",
              height: "64px",
              filter: "drop-shadow(0px 0px 3px #B8722A)",
              color: readyToRefresh ? "primary.main" : "rgba(0,0,0,0.75)",
              transform: `rotate(${rotationDeg}deg)`,
              transition: isPulling
                ? "none"
                : "transform 120ms linear, color 160ms ease-in-out"
            }}
          />
        </div>
      )}
    </div>
  );
};

export default PullToRefreshComponent;
